import React, { createContext, useContext, useState, useEffect } from 'react';
import websocketService from '../services/websocket';

// Create a context for the WebSocket service
const WebSocketContext = createContext(null);

/**
 * Provider component that makes WebSocket service available to any
 * child component that calls useWebSocket().
 */
export const WebSocketProvider = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState(websocketService.getConnectionState());
  const [isAuthenticated, setIsAuthenticated] = useState(websocketService.isAuthenticated());
  const [error, setError] = useState(null);

  useEffect(() => {
    // Set up event listeners for connection state changes
    const handleConnect = () => {
      setConnectionStatus(websocketService.getConnectionState());
      // Clear error when connection succeeds
      setError(null);
    };

    const handleDisconnect = () => {
      setConnectionStatus(websocketService.getConnectionState());
    };

    const handleAuthenticated = () => {
      setIsAuthenticated(true);
      // Clear error when authentication succeeds
      setError(null);
    };

    const handleAuthError = (data) => {
      setIsAuthenticated(false);
      // Set authentication error
      setError(data.message || 'Authentication failed');
    };
    
    const handleError = (errorData) => {
      // Set general WebSocket error
      // console.log('WebSocket error in context:', errorData);
      setError(errorData.message || 'WebSocket connection error');
    };

    // Register event listeners
    websocketService.on('connect', handleConnect);
    websocketService.on('disconnect', handleDisconnect);
    websocketService.on('authenticated', handleAuthenticated);
    websocketService.on('authentication_error', handleAuthError);
    websocketService.on('error', handleError);

    // Clean up event listeners on unmount
    return () => {
      websocketService.off('connect', handleConnect);
      websocketService.off('disconnect', handleDisconnect);
      websocketService.off('authenticated', handleAuthenticated);
      websocketService.off('authentication_error', handleAuthError);
      websocketService.off('error', handleError);
    };
  }, []);

  // The value provided to consumers of the context
  const value = {
    websocketService,
    connectionStatus,
    isAuthenticated,
    error,
    setError,
    connect: websocketService.connect.bind(websocketService),
    disconnect: websocketService.disconnect.bind(websocketService),
    send: websocketService.send.bind(websocketService),
    on: websocketService.on.bind(websocketService),
    off: websocketService.off.bind(websocketService),
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Custom hook to access the WebSocket service from any component
 */
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketContext; 